import {
  InvalidTokenAlert,
  TokenExpiredAlert,
  UnexpectedErrorAlert,
  UserNotFoundAlert,
} from '@/components/alerts';
import { PasswordInput } from '@/components/PasswordInput';
import { PasswordRulesPopover } from '@/components/PasswordRulesPopover';
import { SubmitButton } from '@/components/SubmitButton';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { AccountCenterBox } from '@/layout/AccountCenterBox';
import { api } from '@/services/api';
import {
  ActivateAccountResponse,
  ActivateAccountResultEnum,
  ValidateActivationTokenResponse,
  ValidateActivationTokenResultEnum,
} from '@shared/types';
import { Field, Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';

enum FormStatus {
  NoAlert = 'NoAlert',
  SystemError = 'SystemError',
  InvalidToken = 'InvalidToken',
  TokenExpired = 'TokenExpired',
  UserNotFound = 'UserNotFound',
}

enum PageState {
  Loading = 'Loading',
  Valid = 'Valid',
  Invalid = 'Invalid',
}

export const Activation = () => {
  useDocumentTitle('Marshall Reddick – Activate Account');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = decodeURIComponent(searchParams.get('id') ?? '');

  const [pageState, setPageState] = useState<PageState>(PageState.Loading);
  const [isPasswordValid, setIsPasswordValid] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        navigate('/login', { state: { status: 'ActivationLinkExpired' } });
        return;
      }

      try {
        const response = await api.get<ValidateActivationTokenResponse>('/auth/activate-account', {
          params: { id: token },
        });

        const { result } = response.data;

        if (result === ValidateActivationTokenResultEnum.VALID) {
          setPageState(PageState.Valid);
        } else {
          navigate('/login', { state: { status: 'ActivationLinkExpired' } });
        }
      } catch {
        navigate('/login', { state: { status: 'ActivationLinkExpired' } });
      }
    };

    validateToken();
  }, [token]);

  const alertMap: Partial<Record<FormStatus, React.ReactNode>> = {
    [FormStatus.SystemError]: <UnexpectedErrorAlert />,
    [FormStatus.InvalidToken]: <InvalidTokenAlert />,
    [FormStatus.TokenExpired]: <TokenExpiredAlert />,
    [FormStatus.UserNotFound]: <UserNotFoundAlert />,
  };

  if (pageState === PageState.Loading) {
    return (
      <AccountCenterBox title="Activate your Account" isSubmitting={true}>
        <div className="text-center py-8">
          <p>Validating your activation link...</p>
        </div>
      </AccountCenterBox>
    );
  }

  if (pageState === PageState.Invalid) {
    return null;
  }

  return (
    <Formik
      initialValues={{
        password: '',
        passwordConfirmation: '',
      }}
      initialStatus={FormStatus.NoAlert}
      validationSchema={Yup.object().shape({
        password: Yup.string(),
        passwordConfirmation: Yup.string(),
      })}
      validateOnMount={false}
      onSubmit={async (values, { setFieldError, setStatus }) => {
        if (!values.password.trim()) {
          setFieldError('password', 'Password is required');
        }
        if (!values.password.trim() || !isPasswordValid) {
          return;
        }
        setStatus(FormStatus.NoAlert);

        try {
          const response = await api.post<ActivateAccountResponse>('/auth/activate-account', {
            verificationToken: token,
            password: values.password,
          });

          const { result } = response.data;

          if (result === ActivateAccountResultEnum.SUCCESS) {
            navigate('/login', { state: { status: 'AccountVerified' } });
          } else if (result === ActivateAccountResultEnum.INVALID_TOKEN) {
            setStatus(FormStatus.InvalidToken);
          } else if (result === ActivateAccountResultEnum.TOKEN_EXPIRED) {
            setStatus(FormStatus.TokenExpired);
          } else if (result === ActivateAccountResultEnum.USER_NOT_FOUND) {
            setStatus(FormStatus.UserNotFound);
          } else {
            setStatus(FormStatus.SystemError);
          }
        } catch {
          setStatus(FormStatus.SystemError);
        }
      }}
    >
      {({ errors, touched, values, isSubmitting, status }) => {
        const statusKey = status as FormStatus;
        return (
          <AccountCenterBox
            title="Activate your Account"
            description="To finish activating your account, please set a password below."
            isSubmitting={isSubmitting}
          >
            <Form noValidate>
              {statusKey in alertMap ? alertMap[statusKey] : null}
              {/* PASSWORD */}
              <Field
                name="password"
                as={PasswordInput}
                error={errors.password}
                touched={touched.password}
              />
              {/* PASSWORD CONFIRMATION */}
              <Field
                name="passwordConfirmation"
                label="Confirm Password"
                as={PasswordInput}
                error={errors.passwordConfirmation}
                touched={touched.passwordConfirmation}
              />
              <PasswordRulesPopover
                password={values.password}
                confirm={values.passwordConfirmation}
                onValidityChange={setIsPasswordValid}
              />
              {/* BUTTON */}
              <SubmitButton text="Submit" isSubmitting={isSubmitting} disabled={!isPasswordValid} />
            </Form>
          </AccountCenterBox>
        );
      }}
    </Formik>
  );
};
